import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    getMySubscription,
    refreshUserProfile
} from '../services/subscriptionService';
import layoutStyles from '../styles/modules/PageLayout.module.css';
import headerStyles from '../styles/modules/Header.module.css';
import cardStyles from '../styles/modules/Card.module.css';
import buttonStyles from '../styles/modules/Button.module.css';
import styles from '../styles/modules/CheckoutSuccess.module.css';
import backgroundImage from '../images/background.jpg';

type CheckoutState = 'processing' | 'success' | 'failed' | 'cancelled';

const CheckoutSuccess: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const [state, setState] = useState<CheckoutState>('processing');
    const [errorMessage, setErrorMessage] = useState('');

    const isCanceledFromQuery = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('cancelled') === '1' || params.get('status') === 'cancelled';
    }, [location.search]);

    useEffect(() => {
        const token = localStorage.getItem('authToken');

        if (!token) {
            navigate('/signin');
            return;
        }

        if (isCanceledFromQuery) {
            setState('cancelled');
            return;
        }

        let isActive = true;

        const pollSubscription = async () => {
            const maxAttempts = 10;

            for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
                try {
                    const subscription = await getMySubscription(token);

                    if (subscription.status === 'active') {
                        await refreshUserProfile(token);

                        if (isActive) {
                            setState('success');
                            window.setTimeout(() => navigate('/dashboard'), 1500);
                        }
                        return;
                    }
                } catch (error) {
                    if (isActive) {
                        setState('failed');
                        setErrorMessage(error instanceof Error ? error.message : 'Failed to verify subscription status');
                    }
                    return;
                }

                await new Promise((resolve) => setTimeout(resolve, 3000));
            }

            if (isActive) {
                setState('failed');
                setErrorMessage(t('subscription.statusPending') || 'Payment received, but activation is still pending. Please re-login shortly.');
            }
        };

        pollSubscription();

        return () => {
            isActive = false;
        };
    }, [navigate, t, isCanceledFromQuery]);

    return (
        <div className={layoutStyles.page}>
            <header className={headerStyles.header}>
                <h1 className={headerStyles.title}>{t('subscription.checkoutReturnTitle') || 'Subscription Status'}</h1>
            </header>

            <div className={layoutStyles.container} style={{ backgroundImage: `url(${backgroundImage})` }}>
                <div className={layoutStyles.content}>
                    <div className={`${cardStyles.cardGlass} ${styles.centered}`}>
                        {state === 'processing' && (
                            <>
                                <h2>{t('subscription.processingTitle') || 'Processing payment...'}</h2>
                                <p>{t('subscription.processingHint') || 'Please wait while we verify your subscription.'}</p>
                            </>
                        )}

                        {state === 'success' && (
                            <>
                                <h2>{t('subscription.successTitle') || 'Subscription activated'}</h2>
                                <p>{t('subscription.successHint') || 'Redirecting to dashboard...'}</p>
                                <button type="button" className={buttonStyles.primary} onClick={() => navigate('/dashboard')}>
                                    {t('subscription.goToDashboard') || 'Go to Dashboard'}
                                </button>
                            </>
                        )}

                        {state === 'cancelled' && (
                            <>
                                <h2>{t('subscription.cancelledTitle') || 'Checkout cancelled'}</h2>
                                <p>{t('subscription.cancelledHint') || 'You can retry subscription checkout from Settings.'}</p>
                                <button type="button" className={buttonStyles.primary} onClick={() => navigate('/settings?tab=billing')}>
                                    {t('subscription.retryCheckout') || 'Retry Checkout'}
                                </button>
                            </>
                        )}

                        {state === 'failed' && (
                            <>
                                <h2>{t('subscription.failedTitle') || 'Unable to confirm subscription'}</h2>
                                <p>{errorMessage}</p>
                                <div className={styles.actions}>
                                    <button type="button" className={buttonStyles.primary} onClick={() => navigate('/signin')}>
                                        {t('subscription.reloginCta') || 'Re-login'}
                                    </button>
                                    <button type="button" className={buttonStyles.secondary} onClick={() => navigate('/settings?tab=billing')}>
                                        {t('settings.billingTab') || 'Billing'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutSuccess;
